import { LoggerService, SharedUserService } from '@agroone-front/shared'
import {
  CroptypeColor,
  ForecastDay,
  MsSqlPaginatedData,
  StandCountsFactor,
  TargetLeave,
  UserDto,
  WeekFactor,
} from '@agroone/entities'
import { HttpClient } from '@angular/common/http'
import { computed, inject, Injectable, Signal, signal } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'
import { environment } from '@front-app-environments/environment'
import { getISOWeek, getYear } from 'date-fns'
import { BehaviorSubject, catchError, forkJoin, map, mergeMap, Observable, of } from 'rxjs'
import { RegionService } from '../region/services/region.service'

import configureMeasurements, { allMeasures, type LengthUnits } from 'convert-units'

const convert = configureMeasurements(allMeasures)

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private http = inject(HttpClient)
  private logger = inject(LoggerService)
  private userService = inject(SharedUserService)
  private regionService = inject(RegionService)

  public cropTypeColors?: CroptypeColor[]

  private settingsUrl: string = `${environment.apiUrl}${environment.settings}`

  /**
   * Maturity-only settings (week factors, target leaves, stand counts).
   * Lazily loaded the first time a screen that needs them calls `loadMaturitySettings()`,
   * then cached for the whole app lifetime: the resource is keyed on the region id, which is
   * stable for the session, so it never refetches.
   */
  private readonly maturityRegionId = signal<number | null>(null)

  private readonly maturitySettingsResource = rxResource({
    params: () => this.maturityRegionId() ?? undefined,
    stream: ({ params: regionId }) =>
      forkJoin({
        weekFactors: this.http
          .get<MsSqlPaginatedData<WeekFactor>>(`${this.settingsUrl}/week-factors?filter=regionId=${regionId}`)
          .pipe(map((v) => v.data)),
        targetLeaves: this.http
          .get<MsSqlPaginatedData<TargetLeave>>(`${this.settingsUrl}/target-leaves?filter=regionId=${regionId}`)
          .pipe(map((v) => v.data)),
        standCountsFactors: this.http
          .get<MsSqlPaginatedData<StandCountsFactor>>(`${this.settingsUrl}/stand-counts?filter=regionId=${regionId}`)
          .pipe(map((v) => v.data)),
      }),
    defaultValue: { weekFactors: [], targetLeaves: [], standCountsFactors: [] },
  })

  public readonly weekFactors: Signal<WeekFactor[]> = computed(() => this.maturitySettingsResource.value().weekFactors)
  public readonly targetLeaves: Signal<TargetLeave[]> = computed(
    () => this.maturitySettingsResource.value().targetLeaves,
  )
  public readonly standCountsFactors: Signal<StandCountsFactor[]> = computed(
    () => this.maturitySettingsResource.value().standCountsFactors,
  )

  /**
   * Trigger the lazy load of the maturity settings for the given region.
   * Idempotent: calling it again with the same region is a no-op (already cached).
   */
  public loadMaturitySettings(regionId: number): void {
    if (regionId != null) {
      this.maturityRegionId.set(regionId)
    }
  }

  public get forecastDays(): ForecastDay[] | null {
    return this.forecastDaysSubject.getValue()
  }

  private forecastDaysSubject = new BehaviorSubject<ForecastDay[] | null>(null)

  init(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.userService.user
        .pipe(
          mergeMap((user: UserDto | null) => (user ? this.getCropTypeColors() : of([] as CroptypeColor[]))),
          catchError((error) => {
            this.logger.error('Settings loading error', JSON.stringify(error))
            return of([] as CroptypeColor[])
          }),
        )
        .subscribe((croptypeColors: CroptypeColor[]) => {
          this.cropTypeColors = croptypeColors
          this.logger.log('Settings initialized')
          resolve()
        })
    })
  }

  public getCurrentCampaign(): string {
    const weekNumber: number = getISOWeek(new Date())
    let year: number = getYear(new Date())

    const letterCampaign: string = this.getCampaignLetter(weekNumber)
    if (weekNumber >= 44 && weekNumber <= 52) {
      year += 1
    }
    const campaignYearTwoDigits: string = year.toString().substring(2)

    return `${campaignYearTwoDigits}${letterCampaign}`
  }

  private getCropTypeColors(): Observable<CroptypeColor[]> {
    return this.http
      .get<MsSqlPaginatedData<CroptypeColor>>(`${this.settingsUrl}/croptype-colors`)
      .pipe(map((v) => v.data))
  }

  public getAllForecastDays(): Observable<ForecastDay[]> {
    return this.http.get<MsSqlPaginatedData<ForecastDay>>(`${this.settingsUrl}/forecast-days`).pipe(map((v) => v.data))
  }

  public getForecastDaysFromUsersRegions(): Observable<ForecastDay[]> {
    return this.getAllForecastDays().pipe(
      map((forecastDays: ForecastDay[]) => {
        const forecastDaysFromUsersRegions: ForecastDay[] = forecastDays.filter(
          (f: ForecastDay) =>
            f.region !== undefined && this.userService.requireCurrentUser().availableRegions.includes(f.region),
        )
        this.forecastDaysSubject.next(forecastDaysFromUsersRegions)
        return forecastDaysFromUsersRegions
      }),
    )
  }

  public saveForecastDays(regionId: number, numberOfDays: number, id?: number): Observable<ForecastDay> | undefined {
    if (!regionId || !numberOfDays) {
      return
    }
    if (id) {
      return this.http.put<ForecastDay>(`${this.settingsUrl}/forecast-days`, {
        id,
        regionId,
        numberOfDays,
      })
    } else {
      return this.http.post<ForecastDay>(`${this.settingsUrl}/forecast-days`, { regionId, numberOfDays })
    }
  }

  public delete(id: number): Observable<ForecastDay> {
    return this.http.delete<ForecastDay>(`${this.settingsUrl}/forecast-days/${id}`)
  }

  public getStandCountsFactor(
    cropType: string | undefined,
    bedWidth: number | undefined,
    bedWidthUnit: string | undefined,
    seedLines: number | undefined,
  ): number | undefined {
    if (!cropType || !bedWidth || !bedWidthUnit || !seedLines) {
      return
    }
    return this.standCountsFactors().find((standCountsFactorItem: StandCountsFactor) => {
      let bedWidthValue = standCountsFactorItem.bedWidth
      if (standCountsFactorItem.bedWidthUnit !== bedWidthUnit) {
        bedWidthValue = convert(bedWidthValue as number)
          // length unit codes come from the API constants, not checked against convert-units
          .from(standCountsFactorItem.bedWidthUnit as LengthUnits)
          .to(bedWidthUnit as LengthUnits)
      }

      return (
        cropType === standCountsFactorItem.croptype &&
        seedLines === standCountsFactorItem.seedLines &&
        bedWidthValue === bedWidth
      )
    })?.standCountsFactor
  }

  public getTargetLeaves(cropType: string): number | undefined {
    return this.targetLeaves().find((targetLeavesItem: TargetLeave) => cropType === targetLeavesItem.croptype)
      ?.targetLeaves
  }

  public getWeekFactor(cropType: string, interventionWeek: number, maturityWeek: number): number {
    return (
      this.weekFactors().find(
        (weekFactorItem: WeekFactor) =>
          cropType === weekFactorItem.croptype &&
          maturityWeek === weekFactorItem.maturityWeek &&
          interventionWeek === weekFactorItem.interventionWeek,
      )?.weekFactor || 0
    )
  }

  private getCampaignLetter(weekNumber: number) {
    return weekNumber >= 18 && weekNumber <= 43 ? 'E' : 'H'
  }
}
